
import React, { useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/auth';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from '../ui/dropdown-menu';
import { LogOut, User, FileText, FilePlus, LayoutDashboard, DollarSign, Menu, X } from 'lucide-react';

const MainLayout: React.FC = () => {
  const { user, signOut, isAdmin, checkAdminStatus } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);

  useEffect(() => {
    // Check admin status when component mounts
    if (user) {
      checkAdminStatus();
    }
  }, [user, checkAdminStatus]);

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase();
  };

  const navLinks = [
    { name: 'หน้าหลัก', path: '/', icon: <span className="mr-2">🏠</span> },
    { name: 'รายงานทั้งหมด', path: '/teacher-reports', icon: <FileText className="h-4 w-4 mr-2" /> },
    { name: 'สร้างรายงาน', path: '/form', icon: <FilePlus className="h-4 w-4 mr-2" /> },
    { name: 'เงินเดือนครู', path: '/salary', icon: <DollarSign className="h-4 w-4 mr-2" /> },
    // คลาสเฉพาะกิจ link removed from here
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <header className="bg-form-blue text-white py-4 shadow-md">
        <div className="container max-w-6xl mx-auto px-4 flex justify-between items-center">
          <div className="flex items-center space-x-6">
            <Link to="/" className="text-xl md:text-2xl font-bold">Fishsix</Link>
            {user && (
              <nav className="hidden md:flex space-x-4">
                {navLinks.map((link) => (
                  <Link 
                    key={link.path} 
                    to={link.path} 
                    className={`hover:text-blue-200 transition-colors px-2 py-1 rounded-md ${
                      location.pathname === link.path ? 'bg-blue-700 text-white' : ''
                    }`}
                  >
                    <span className="flex items-center">{link.icon} {link.name}</span>
                  </Link>
                ))}
                {isAdmin && (
                  <Link 
                    to="/admin" 
                    className={`hover:text-blue-200 transition-colors font-semibold px-2 py-1 rounded-md ${
                      location.pathname.startsWith('/admin') ? 'bg-blue-700 text-white' : ''
                    }`}
                  >
                    <span className="flex items-center"><LayoutDashboard className="h-4 w-4 mr-2" /> แผงควบคุมผู้ดูแล</span>
                  </Link>
                )}
              </nav>
            )}
          </div>
          
          {user ? (
            <div className="flex items-center">
              <Button 
                variant="ghost" 
                size="icon" 
                className="md:hidden text-white hover:bg-blue-700 mr-2" 
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              >
                {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </Button>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="rounded-full h-10 w-10 p-0">
                    <Avatar>
                      <AvatarFallback className="bg-blue-700">
                        {user?.user_metadata?.full_name ? 
                          getInitials(user.user_metadata.full_name) : 
                          user.email?.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <div className="p-2">
                    <p className="font-medium">
                      {user?.user_metadata?.full_name || 'ผู้ใช้งาน'}
                    </p>
                    <p className="text-sm text-gray-500">{user.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/')}>
                    <span className="mr-2">🏠</span> หน้าหลัก
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/teacher-reports')}>
                    <FileText className="mr-2 h-4 w-4" />
                    <span>รายงานทั้งหมด</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/form')}>
                    <FilePlus className="mr-2 h-4 w-4" />
                    <span>สร้างรายงาน</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/salary')}>
                    <DollarSign className="mr-2 h-4 w-4" />
                    <span>เงินเดือนครู</span>
                  </DropdownMenuItem>
                  {/* คลาสเฉพาะกิจ menu item removed from dropdown */}
                  {isAdmin && (
                    <DropdownMenuItem onClick={() => navigate('/admin')}>
                      <LayoutDashboard className="mr-2 h-4 w-4" />
                      <span>แผงควบคุมผู้ดูแล</span>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => navigate('/profile')}>
                    <User className="mr-2 h-4 w-4" />
                    <span>โปรไฟล์</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => signOut()}>
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>ออกจากระบบ</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : (
            <Button variant="secondary" onClick={() => navigate('/auth')}>
              เข้าสู่ระบบ
            </Button>
          )}
        </div>
      </header>

      {/* Mobile Navigation Menu */}
      {isMobileMenuOpen && user && (
        <div className="md:hidden bg-blue-600 border-t border-blue-500">
          <nav className="flex flex-col space-y-1 px-4 py-3">
            {navLinks.map((link) => (
              <Link 
                key={link.path} 
                to={link.path} 
                className={`text-white hover:bg-blue-700 px-3 py-2 rounded-md flex items-center ${
                  location.pathname === link.path ? 'bg-blue-700 font-semibold' : ''
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.icon} {link.name}
              </Link>
            ))}
            {isAdmin && (
              <Link 
                to="/admin" 
                className={`text-white hover:bg-blue-700 px-3 py-2 rounded-md flex items-center font-semibold ${
                  location.pathname.startsWith('/admin') ? 'bg-blue-700' : ''
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <LayoutDashboard className="h-4 w-4 mr-2" /> แผงควบคุมผู้ดูแล
              </Link>
            )}
          </nav>
        </div>
      )}

      <main className="flex-grow">
        <Outlet />
      </main>

      <footer className="bg-gray-100 border-t border-gray-200 py-6 mt-auto">
        <div className="container max-w-4xl mx-auto px-4 text-center text-gray-500 text-sm">
          © {new Date().getFullYear()} แบบฟอร์มรายงานการสอน
        </div>
      </footer>
    </div>
  );
};

export default MainLayout;
